/**
 * CalendarView Component
 * Full calendar view for managing meetings
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar as CalendarIcon, List } from 'lucide-react';
import { Meeting, MeetingFormData } from '@/types/calendar.types';
import { CALENDAR_TRANSLATIONS } from '@/constants/calendar.constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCalendar } from '@/hooks/useCalendar';
import { MeetingCard } from './MeetingCard';
import { MeetingFormDialog } from './MeetingFormDialog';
import { MeetingDetailDialog } from './MeetingDetailDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface CalendarViewProps {
  dealId?: string;
  showHeader?: boolean;
}

export function CalendarView({ dealId, showHeader = true }: CalendarViewProps) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = CALENDAR_TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];
  
  const {
    meetings,
    loading,
    scheduleMeeting,
    updateMeeting,
    updateMeetingStatus,
    deleteMeeting,
    getUpcomingMeetings,
    getPastMeetings,
  } = useCalendar(dealId);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [detailMeeting, setDetailMeeting] = useState<Meeting | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null);
  const [isReschedule, setIsReschedule] = useState(false);

  const upcomingMeetings = getUpcomingMeetings();
  const pastMeetings = getPastMeetings();

  const handleSubmit = async (data: MeetingFormData) => {
    if (isReschedule && editingMeeting) {
      // Mark original as rescheduled then create new
      await updateMeetingStatus(editingMeeting.id, 'cancelled');
      await scheduleMeeting(data);
    } else if (editingMeeting) {
      await updateMeeting(editingMeeting.id, data);
    } else {
      await scheduleMeeting(data);
    }
    setEditingMeeting(null);
    setIsReschedule(false);
  };

  const handleEdit = (meeting: Meeting) => {
    setEditingMeeting(meeting);
    setIsReschedule(false);
    setIsFormOpen(true);
  };

  const handleReschedule = (meeting: Meeting) => {
    setEditingMeeting(meeting);
    setIsReschedule(true);
    setIsFormOpen(true);
  };

  const handleConfirm = async (id: string) => {
    await updateMeetingStatus(id, 'confirmed');
  };

  const handleCancel = async () => {
    if (cancelConfirm) {
      await updateMeetingStatus(cancelConfirm, 'cancelled');
      setCancelConfirm(null);
    }
  };

  const handleComplete = async (id: string) => {
    await updateMeetingStatus(id, 'completed');
  };

  const handleDelete = async () => {
    if (deleteConfirm) {
      await deleteMeeting(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleJoin = (meeting: Meeting) => {
    if (meeting.meetingUrl) {
      window.open(meeting.meetingUrl, '_blank');
    }
  };

  const handleNavigate = (meeting: Meeting) => {
    setDetailMeeting(meeting);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t.title}</h1>
            <p className="text-muted-foreground">{t.subtitle}</p>
          </div>
          <Button onClick={() => { setEditingMeeting(null); setIsReschedule(false); setIsFormOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            {t.newMeeting}
          </Button>
        </div>
      )}

      <Tabs defaultValue="upcoming" className="w-full">
        <TabsList>
          <TabsTrigger value="upcoming" className="gap-2">
            <CalendarIcon className="w-4 h-4" />
            {t.upcomingMeetings} ({upcomingMeetings.length})
          </TabsTrigger>
          <TabsTrigger value="past" className="gap-2">
            <List className="w-4 h-4" />
            {t.pastMeetings} ({pastMeetings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-4">
          {upcomingMeetings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CalendarIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t.noMeetings}</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => { setEditingMeeting(null); setIsFormOpen(true); }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t.scheduleMeeting}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcomingMeetings.map((meeting) => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  onConfirm={handleConfirm}
                  onCancel={(id) => setCancelConfirm(id)}
                  onComplete={handleComplete}
                  onEdit={handleEdit}
                  onDelete={(id) => setDeleteConfirm(id)}
                  onJoin={handleJoin}
                  onReschedule={handleReschedule}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-4">
          {pastMeetings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t.noMeetings}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pastMeetings.map((meeting) => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  onDelete={(id) => setDeleteConfirm(id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <MeetingFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSubmit={handleSubmit}
        meeting={editingMeeting}
        defaultDealId={dealId}
        isReschedule={isReschedule}
      />

      {/* Detail Dialog */}
      <MeetingDetailDialog
        meeting={detailMeeting}
        open={!!detailMeeting}
        onOpenChange={(open) => { if (!open) setDetailMeeting(null); }}
      />

      {/* Cancel Confirmation */}
      <AlertDialog open={!!cancelConfirm} onOpenChange={() => setCancelConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.cancel}</AlertDialogTitle>
            <AlertDialogDescription>{t.confirmCancel}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'fr' ? 'Non' : 'No'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-destructive text-destructive-foreground">
              {t.cancel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.delete}</AlertDialogTitle>
            <AlertDialogDescription>{t.confirmDelete}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'fr' ? 'Non' : 'No'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
